import { useState } from 'react'
import { Copy, Check, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'

interface MfaRecoveryCodesDisplayProps {
  codes: string[]
  email: string
  onConfirm: () => void
  confirmLabel?: string
  /** When true, renders the "I have saved..." checkbox + Done button. */
  requireConfirmation?: boolean
}

function pad(n: number): string {
  return n.toString().padStart(2, '0')
}

function timestampedFilename(email: string): string {
  const now = new Date()
  const datePart = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
  const timePart = `${pad(now.getHours())}${pad(now.getMinutes())}`
  const safeEmail = email.replace(/[^a-zA-Z0-9._-]/g, '_')
  return `bidcontext-recovery-codes-${datePart}-${timePart}-${safeEmail}.txt`
}

export function MfaRecoveryCodesDisplay({
  codes,
  email,
  onConfirm,
  confirmLabel = 'Done',
  requireConfirmation = true,
}: MfaRecoveryCodesDisplayProps) {
  const [copied, setCopied] = useState(false)
  const [acknowledged, setAcknowledged] = useState(!requireConfirmation)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access can fail in non-secure contexts; user can still
      // download the file as a fallback.
    }
  }

  const handleDownload = () => {
    const generated = new Date().toISOString()
    const header = `BidContext AI — Recovery Codes for ${email} — Generated ${generated}\n`
    const help = `Each code works once. Use them if you lose access to your authenticator app.\n\n`
    const body = codes.join('\n')
    const blob = new Blob([header + help + body + '\n'], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = timestampedFilename(email)
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium">Save your recovery codes</h3>
        <p className="text-meta mt-1">
          Use these if you lose access to your authenticator. Each code works once.
          Treat them like passwords — store somewhere safe (a password manager is ideal).
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-muted font-mono text-sm">
        {codes.map((code) => (
          <div key={code} className="text-center select-all">
            {code}
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => void handleCopy()}>
          {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
          {copied ? 'Copied' : 'Copy all'}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={handleDownload}>
          <Download className="h-4 w-4 mr-2" />
          Download .txt
        </Button>
      </div>

      {requireConfirmation && (
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={acknowledged}
            onCheckedChange={(v) => setAcknowledged(v === true)}
            className="mt-0.5"
          />
          <span>I have saved these codes in a safe place</span>
        </label>
      )}

      <div className="flex justify-end">
        <Button type="button" disabled={requireConfirmation && !acknowledged} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  )
}
